/**
 * Compact reference documentation for popular OpenSCAD libraries
 * Used in AI prompts to help models use libraries correctly
 */

export const LIBRARY_REFERENCE = `
### BOSL2 Library (include <BOSL2/std.scad>)

IMPORTANT: Use \`include\` (not \`use\`) for BOSL2 to access all features.

**Shapes:**
- \`cuboid([x,y,z], rounding=r, edges=edges)\` - Rounded cuboid
- \`cyl(h, r|d, rounding=r, rounding1=r1, rounding2=r2)\` - Rounded cylinder
- \`sphere(r|d)\` - Sphere with better $fn handling
- \`prismoid(size1, size2, h, rounding=r)\` - Tapered prism
- \`tube(h, ir, or)\` or \`tube(h, id, od)\` - Hollow tube

**Transforms:**
- \`up(z)\`, \`down(z)\`, \`left(x)\`, \`right(x)\`, \`fwd(y)\`, \`back(y)\` - Directional moves
- \`xrot(a)\`, \`yrot(a)\`, \`zrot(a)\` - Axis rotations
- \`xscale(s)\`, \`yscale(s)\`, \`zscale(s)\` - Axis scaling

**Attachments:**
- \`attach(from, to)\` - Attach child to parent anchor
- \`position(anchor)\` - Position at anchor without rotation
- Anchors: CENTER, TOP, BOTTOM, LEFT, RIGHT, FRONT, BACK

**Paths & Sweeps:**
- \`path_sweep(shape, path)\` - Sweep 2D shape along path
- \`linear_sweep(region, h)\` - Better linear_extrude
- \`rotate_sweep(shape, angle)\` - Better rotate_extrude

**Distributors:**
- \`xcopies(spacing, n)\` - Copies along X
- \`ycopies(spacing, n)\` - Copies along Y
- \`zcopies(spacing, n)\` - Copies along Z
- \`grid_copies(spacing, n)\` - 2D grid of copies

### MCAD Library

**Regular Shapes (include <MCAD/regular_shapes.scad>):**
- \`hexagon(size)\` - Regular hexagon
- \`octagon(size)\` - Regular octagon
- \`ellipse(width, height)\` - Ellipse shape
- \`egg_outline(length, width)\` - Egg shape outline

**Boxes (include <MCAD/boxes.scad>):**
- \`roundedBox([x,y,z], radius)\` - Box with rounded edges

**Threads (include <MCAD/threads.scad>):**
- \`metric_thread(diameter, pitch, length)\` - Metric screw thread
- \`english_thread(diameter, threads_per_inch, length)\` - Imperial thread

**Gears (include <MCAD/involute_gears.scad>):**
- \`gear(number_of_teeth, circular_pitch)\` - Involute gear
- \`bevel_gear(number_of_teeth, cone_distance)\` - Bevel gear

### LEGO Library (use <LEGO/LEGO.scad>)

**Main Module:**
- \`block(width, length, height, type="brick")\`
  - \`width\`: studs wide
  - \`length\`: studs long
  - \`height\`: 1=brick, 1/3=plate
  - \`type\`: "brick", "tile", "wing", "slope", "curve", "baseplate", "round", "round-tile"

**Examples:**
- \`block(2, 4, 1);\` // 2x4 Brick
- \`block(2, 4, 1/3);\` // 2x4 Plate
- \`block(1, 2, 1, type="tile");\` // 1x2 Tile

### Tips for Using Libraries

1. ALWAYS use \`include\` (NOT \`use\`) for libraries: \`include <BOSL2/std.scad>\`
2. BOSL2 is preferred for modern designs - it's more comprehensive
3. Check if the library is available - not all environments have all libraries
4. Provide fallback implementations for critical features
`.trim();

/**
 * Compact OpenSCAD language reference
 */
export const OPENSCAD_REFERENCE = `
### OpenSCAD Quick Reference

**Primitives:**
- \`cube([x,y,z], center=true)\`
- \`sphere(r=radius, $fn=segments)\`
- \`cylinder(h=height, r=radius, r1=bottom, r2=top, center=true, $fn=segments)\`
- \`polyhedron(points=[[x,y,z],...], faces=[[0,1,2],...])\`

**2D Shapes:**
- \`square([x,y], center=true)\`
- \`circle(r=radius, $fn=segments)\`
- \`polygon(points=[[x,y],...])\`
- \`text("string", size=10, font="Liberation Sans")\`

**Transforms:**
- \`translate([x,y,z])\`
- \`rotate([x,y,z])\` or \`rotate(a, v=[x,y,z])\`
- \`scale([x,y,z])\`
- \`mirror([x,y,z])\`
- \`color([r,g,b,a])\` or \`color("colorname")\`

**Boolean Operations:**
- \`union() { ... }\`
- \`difference() { ... }\` - First child minus rest
- \`intersection() { ... }\`

**Extrusions:**
- \`linear_extrude(height, twist=deg, scale=s, slices=n)\`
- \`rotate_extrude(angle=360, $fn=n)\`

**Modifiers:**
- \`*\` - Disable (comment out)
- \`!\` - Show only this
- \`#\` - Highlight/debug
- \`%\` - Transparent/background

**Control Flow:**
- \`for(i = [start:step:end]) { ... }\`
- \`for(i = [a, b, c]) { ... }\`
- \`if(condition) { ... } else { ... }\`
- \`let(var = value) ...\`

**Modules & Functions:**
- \`module name(param=default) { ... }\`
- \`function name(param) = expression;\`

**Special Variables:**
- \`$fn\` - Number of segments for curves
- \`$fa\` - Minimum angle for segments
- \`$fs\` - Minimum size for segments
`.trim();
